// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { createAction, ActionUnion, ThunkAction } from 'utils/redux';
import { CombinedState } from 'reducers';
import { getCore, Storage } from 'cvat-core-wrapper';
import { LogType } from 'cvat-logger';
import { getProjectsAsync } from './projects-actions';
import { AnnotationActionTypes, fetchAnnotationsAsync } from './annotation-actions';

const core = getCore();

export enum ImportActionTypes {
    OPEN_IMPORT_DATASET_MODAL = 'OPEN_IMPORT_DATASET_MODAL',
    CLOSE_IMPORT_DATASET_MODAL = 'CLOSE_IMPORT_DATASET_MODAL',
    IMPORT_DATASET = 'IMPORT_DATASET',
    IMPORT_DATASET_SUCCESS = 'IMPORT_DATASET_SUCCESS',
    IMPORT_DATASET_FAILED = 'IMPORT_DATASET_FAILED',
    IMPORT_DATASET_UPDATE_STATUS = 'IMPORT_DATASET_UPDATE_STATUS',
    OPEN_IMPORT_BACKUP_MODAL = 'OPEN_IMPORT_BACKUP_MODAL',
    CLOSE_IMPORT_BACKUP_MODAL = 'CLOSE_IMPORT_BACKUP_MODAL',
    IMPORT_BACKUP = 'IMPORT_BACKUP',
    IMPORT_BACKUP_SUCCESS = 'IMPORT_BACKUP_SUCCESS',
    IMPORT_BACKUP_FAILED = 'IMPORT_BACKUP_FAILED',
}

export const importActions = {
    openImportDatasetModal: (instance: any, isReference: boolean = false) => createAction(ImportActionTypes.OPEN_IMPORT_DATASET_MODAL, { instance, isReference }),
    closeImportDatasetModal: (instance: any) => createAction(ImportActionTypes.CLOSE_IMPORT_DATASET_MODAL, { instance }),
    importDataset: (instance: any, format: string) => createAction(ImportActionTypes.IMPORT_DATASET, { instance, format }),
    importDatasetSuccess: (instance: any, resource: 'dataset' | 'annotation' | 'reference') => createAction(ImportActionTypes.IMPORT_DATASET_SUCCESS, { instance, resource }),
    importDatasetFailed: (instance: any, resource: 'dataset' | 'annotation' | 'reference', error: any) => createAction(ImportActionTypes.IMPORT_DATASET_FAILED, {
        instance,
        resource,
        error,
    }),
    importDatasetUpdateStatus: (instance: any, progress: number, status: string) => createAction(ImportActionTypes.IMPORT_DATASET_UPDATE_STATUS, { instance, progress, status }),
    openImportBackupModal: (instanceType: 'project' | 'task' | 'reference') => createAction(ImportActionTypes.OPEN_IMPORT_BACKUP_MODAL, { instanceType }),
    closeImportBackupModal: (instanceType: 'project' | 'task' | 'reference') => createAction(ImportActionTypes.CLOSE_IMPORT_BACKUP_MODAL, { instanceType }),
    importBackup: () => createAction(ImportActionTypes.IMPORT_BACKUP),
    importBackupSuccess: (instanceId: number, instanceType: 'project' | 'task' | 'reference') => createAction(ImportActionTypes.IMPORT_BACKUP_SUCCESS, { instanceId, instanceType }),
    importBackupFailed: (instanceType: 'project' | 'task' | 'reference', error: any) => createAction(ImportActionTypes.IMPORT_BACKUP_FAILED, { instanceType, error }),
};

export const importDatasetAsync =
    (
        instance: any,
        format: string,
        useDefaultSettings: boolean,
        sourceStorage: Storage,
        file: File | string,
        convMaskToPoly: boolean,
        isReference: boolean,
    ): ThunkAction => async (dispatch, getState) => {
        let resource: 'dataset' | 'annotation' | 'reference' =
            instance instanceof core.classes.Project ? 'dataset' : 'annotation';
        if (isReference) resource = 'reference';

        try {
            const state: CombinedState = getState();

            if (isReference) {
                dispatch(importActions.importDataset(instance, 'reference'));
                // To-do: логика на загрузку файла на сервер
                await new Promise((resolve) => setTimeout(resolve, 1000)); // Симмуляция загрузки

                throw new Error('Не загружено, нет эндпоинта и логики');
            } else if (instance instanceof core.classes.Project) {
                if (state.import.projects.dataset.current?.[instance.id]) {
                    throw Error('Only one importing of annotation/dataset allowed at the same time');
                }
                dispatch(importActions.importDataset(instance, format));
                await instance.annotations.importDataset(format, useDefaultSettings, sourceStorage, file, {
                    convMaskToPoly,
                    updateStatusCallback: (message: string, progress: number) => dispatch(
                        importActions.importDatasetUpdateStatus(instance, Math.floor(progress * 100), message),
                    ),
                });
            } else if (instance instanceof core.classes.Task) {
                if (state.import.tasks.dataset.current?.[instance.id]) {
                    throw Error('Only one importing of annotation/dataset allowed at the same time');
                }
                dispatch(importActions.importDataset(instance, format));
                await instance.annotations.upload(format, useDefaultSettings, sourceStorage, file, { convMaskToPoly });
            } else if (instance instanceof core.classes.Reference) {
                if (state.import.references.dataset.current?.[instance.id]) {
                    throw Error('Only one importing of annotation/dataset allowed at the same time');
                }
                dispatch(importActions.importDataset(instance, format));
                await instance.annotations.upload(format, useDefaultSettings, sourceStorage, file, { convMaskToPoly });
            } else if (instance instanceof core.classes.ReferenceJob) {
                if (state.import.references.dataset.current?.[instance.referenceId]) {
                    throw Error('Annotations is being uploaded for the reference');
                }
                if (state.import.reference_jobs.dataset.current?.[instance.id]) {
                    throw Error('Only one uploading of annotations for a job allowed at the same time');
                }

                dispatch(importActions.importDataset(instance, format));

                await instance.annotations.upload(format, useDefaultSettings, sourceStorage, file, { convMaskToPoly });
                await instance.logger.log(LogType.uploadAnnotations);
                await instance.annotations.clear(true);
                await instance.actions.clear();
                const history = await instance.actions.get();

                // first set empty objects list
                // to escape some problems in canvas when shape with the same
                // clientID has different type (polygon, rectangle) for example
                dispatch({
                    type: AnnotationActionTypes.UPLOAD_JOB_ANNOTATIONS_SUCCESS,
                    payload: {
                        states: [],
                        history,
                    },
                });

                setTimeout(() => {
                    dispatch(fetchAnnotationsAsync());
                });
            } else {
                // job
                if (state.import.tasks.dataset.current?.[instance.taskId]) {
                    throw Error('Annotations is being uploaded for the task');
                }
                if (state.import.jobs.dataset.current?.[instance.id]) {
                    throw Error('Only one uploading of annotations for a job allowed at the same time');
                }

                dispatch(importActions.importDataset(instance, format));

                await instance.annotations.upload(format, useDefaultSettings, sourceStorage, file, { convMaskToPoly });
                await instance.logger.log(LogType.uploadAnnotations);
                await instance.annotations.clear(true);
                await instance.actions.clear();
                const history = await instance.actions.get();

                // first set empty objects list
                // to escape some problems in canvas when shape with the same
                // clientID has different type (polygon, rectangle) for example
                dispatch({
                    type: AnnotationActionTypes.UPLOAD_JOB_ANNOTATIONS_SUCCESS,
                    payload: {
                        states: [],
                        history,
                    },
                });

                setTimeout(() => {
                    dispatch(fetchAnnotationsAsync());
                });
            }
        } catch (error) {
            dispatch(importActions.importDatasetFailed(instance, resource, error));
            return;
        }

        dispatch(importActions.importDatasetSuccess(instance, resource));
        if (instance instanceof core.classes.Project) {
            dispatch(getProjectsAsync({ id: instance.id }, getState().projects.tasksGettingQuery));
        }
    };

export const importBackupAsync =
    (instanceType: 'project' | 'task' | 'reference', storage: Storage, file: File | string): ThunkAction => async (dispatch) => {
        dispatch(importActions.importBackup());
        try {
            let instanceClass;
            if (instanceType === 'task') instanceClass = core.classes.Task;
            else if (instanceType === 'reference') instanceClass = core.classes.Reference;
            else instanceClass = core.classes.Project;

            const instance = await instanceClass.restore(storage, file);
            dispatch(importActions.importBackupSuccess(instance.id, instanceType));
        } catch (error) {
            dispatch(importActions.importBackupFailed(instanceType, error));
        }
    };

export type ImportActions = ActionUnion<typeof importActions>;
